/**
 * Doctors Service
 * API calls for doctor profile and document operations
 */

import { API_ENDPOINTS } from '@/constants/api';
import { apiClient, ApiResponse } from './apiClient';
import { getAuthToken } from '@/utils/auth';
import { API_CONFIG } from '@/constants/api';

// ─────────────────────────────────────────────────────────────────────────────
// DTOs
// ─────────────────────────────────────────────────────────────────────────────

export interface AvailabilityBlockDTO {
  dayOfWeek: 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';
  startTime: string;
  endTime: string;
  type?: 'TELEMEDICINE' | 'CLINIC' | 'HOME';
}

/**
 * DoctorDocumentDTO — metadata only.
 * Files are accessed via Signed URL (view-url endpoint), never stored directly.
 */
export interface DoctorDocumentDTO {
  id: string;
  doctorId?: string;
  type?: string;
  documentType?:
    | 'CRM'
    | 'SPECIALTY_CERTIFICATE'
    | 'INSCRIPTION_PROOF'
    | 'CONFIRMATION_VIDEO'
    | 'DIGITAL_CERTIFICATE'
    | string;
  fileName?: string;
  originalFileName?: string;
  fileUrl?: string;
  mimeType: string;
  fileSize: number;
  storageProvider?: 'CLOUDFLARE_R2' | 'LOCAL_FILESYSTEM' | string;
  hasFile?: boolean;
  status: 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED' | string;
  rejectionReason?: string;
  reviewedAt?: string;
  reviewedByAdminId?: string;
  createdAt: string;
  updatedAt?: string;
  certificateKind?: 'A1' | 'A2' | 'A3' | 'A4' | null;
  hasCertificatePassword?: boolean;
  requiresPasswordToUnlock?: boolean;
}

export interface DoctorAnalyticsDTO {
  patientsCount: number;
  completedAppointmentsCount: number;
  satisfactionRating: number | null;
  ratingsCount: number;
}

export interface DoctorDTO {
  id: string;
  userId: string;
  crm: string;
  crmState: string;
  specialty: string;
  cpf?: string | null;
  dateOfBirth?: string | null;
  sex?: string | null;
  biography?: string;
  phone?: string;
  clinicAddress?: string;
  officeCep?: string;
  officeStreet?: string;
  officeNumber?: string;
  officeComplement?: string;
  officeNeighborhood?: string;
  officeCity?: string;
  officeState?: string;
  hasClinic?: boolean;
  consultationPrice?: number;
  consultationDuration?: number;
  minAdvanceTelemedicineMinutes?: number;
  minAdvanceOfficeMinutes?: number;
  minAdvanceHomecareMinutes?: number;
  isApproved: boolean;
  isAvailable?: boolean;
  isActive?: boolean;
  /** Status do usuário associado: 'active' | 'suspended' */
  userStatus?: 'active' | 'suspended';
  onCallModes?: string[];
  professionalDocumentsUrl?: string;
  weeklySchedule?: UpdateScheduleDTO;
  status?: string;
  /** URL relativa ou absoluta da foto de perfil */
  avatarUrl?: string;
  hasAvatar?: boolean;
  documents?: DoctorDocumentDTO[];
  analytics?: DoctorAnalyticsDTO;
  availability?: AvailabilityBlockDTO[];
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    email: string;
    name: string;
    status?: 'active' | 'suspended';
  };
}

export interface DocumentViewUrlDTO {
  signedUrl: string;
  fileName: string;
  mimeType: string;
  expiresAt: string;
}

export interface CreateDoctorDTO {
  userId: string;
  crm: string;
  crmState: string;
  specialty: string;
  biography?: string;
  phone?: string;
  clinicAddress?: string;
  hasClinic?: boolean;
  consultationPrice?: number;
}

export interface UpdateDoctorDTO {
  crm?: string;
  crmState?: string;
  specialty?: string;
  biography?: string;
  phone?: string;
  clinicAddress?: string;
  officeStreet?: string;
  officeCity?: string;
  officeState?: string;
  hasClinic?: boolean;
  consultationPrice?: number;
  consultationDuration?: number;
  cpf?: string;
  dateOfBirth?: string;
  sex?: 'M' | 'F' | string;
}

export interface UpdateOfficeAddressDTO {
  officeCep: string;
  officeStreet: string;
  officeNumber: string;
  officeComplement?: string;
  officeNeighborhood: string;
  officeCity: string;
  officeState: string;
}

export interface UpdateScheduleDTO {
  availability: AvailabilityBlockDTO[];
}

export interface UpdateDoctorSchedulingSettingsDTO {
  minAdvanceTelemedicineMinutes?: number;
  minAdvanceOfficeMinutes?: number;
  minAdvanceHomecareMinutes?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Doctor Profile
// ─────────────────────────────────────────────────────────────────────────────

/**
 * List all doctors
 */
export async function listDoctors(
  approved?: boolean,
  search?: string,
  status?: 'active' | 'suspended',
  onlyEligible?: boolean,
): Promise<ApiResponse<DoctorDTO[]>> {
  const params = new URLSearchParams();
  if (approved !== undefined) params.set('approved', String(approved));
  if (search) params.set('search', search);
  if (status) params.set('status', status);
  if (onlyEligible) params.set('onlyEligible', 'true');
  const qs = params.toString();
  const url = qs ? `${API_ENDPOINTS.DOCTORS.LIST}?${qs}` : API_ENDPOINTS.DOCTORS.LIST;
  return apiClient.get(url);
}

export async function getDoctorById(id: string): Promise<ApiResponse<DoctorDTO>> {
  return apiClient.get(API_ENDPOINTS.DOCTORS.GET(id));
}

export async function getDoctorByUserId(userId: string): Promise<ApiResponse<DoctorDTO>> {
  return apiClient.get(API_ENDPOINTS.DOCTORS.GET_BY_USER(userId));
}

export async function getDoctorsBySpecialty(specialty: string): Promise<ApiResponse<DoctorDTO[]>> {
  return apiClient.get(API_ENDPOINTS.DOCTORS.GET_BY_SPECIALTY(specialty));
}

export async function createDoctor(data: CreateDoctorDTO): Promise<ApiResponse<DoctorDTO>> {
  return apiClient.post(API_ENDPOINTS.DOCTORS.CREATE, data);
}

export async function updateDoctor(
  id: string,
  data: UpdateDoctorDTO,
): Promise<ApiResponse<DoctorDTO>> {
  return apiClient.put(API_ENDPOINTS.DOCTORS.UPDATE(id), data);
}

/**
 * Update doctor office address
 */
export async function updateDoctorOfficeAddress(
  id: string,
  data: UpdateOfficeAddressDTO,
): Promise<ApiResponse<DoctorDTO>> {
  return apiClient.patch(API_ENDPOINTS.DOCTORS.UPDATE_OFFICE_ADDRESS(id), data);
}

/**
 * Update doctor schedule
 */
export async function updateDoctorSchedule(
  id: string,
  data: UpdateScheduleDTO,
): Promise<ApiResponse<DoctorDTO>> {
  return apiClient.put(API_ENDPOINTS.DOCTORS.UPDATE_SCHEDULE(id), data);
}

export async function approveDoctor(id: string): Promise<ApiResponse<DoctorDTO>> {
  return apiClient.post(API_ENDPOINTS.DOCTORS.APPROVE(id));
}

export async function updateMySchedulingSettings(
  data: UpdateDoctorSchedulingSettingsDTO,
): Promise<ApiResponse<UpdateDoctorSchedulingSettingsDTO>> {
  return apiClient.patch('/doctors/me/scheduling-settings', data);
}

export async function getDoctorAnalytics(
  doctorId: string,
): Promise<ApiResponse<DoctorAnalyticsDTO>> {
  return apiClient.get(`${API_ENDPOINTS.DOCTORS.GET(doctorId)}/analytics`);
}

// ─────────────────────────────────────────────────────────────────────────────
// Doctor Documents (R2-based)
// ─────────────────────────────────────────────────────────────────────────────

/** List all professional documents (metadata only, no file URLs) */
export async function getDoctorDocuments(
  doctorId: string,
): Promise<ApiResponse<DoctorDocumentDTO[]>> {
  return apiClient.get(`/doctors/${doctorId}/documents`);
}

/** Generate a temporary Signed URL (15 min) to view/download a document */
export async function getDoctorDocumentViewUrl(
  doctorId: string,
  documentId: string,
): Promise<ApiResponse<DocumentViewUrlDTO>> {
  return apiClient.get(`/doctors/${doctorId}/documents/${documentId}/view-url`);
}

/** Upload a new professional document to Cloudflare R2 */
export async function uploadDoctorDocument(
  doctorId: string,
  file: File,
  documentType:
    | 'CRM'
    | 'SPECIALTY_CERTIFICATE'
    | 'INSCRIPTION_PROOF'
    | 'CONFIRMATION_VIDEO'
    | 'DIGITAL_CERTIFICATE',
  options?: {
    certificateKind?: 'A1' | 'A2' | 'A3' | 'A4';
    certificatePassword?: string;
  },
): Promise<ApiResponse<DoctorDocumentDTO>> {
  const formData = new FormData();
  formData.append('documentType', documentType);
  formData.append('type', documentType);
  formData.append('file', file);
  if (options?.certificateKind) {
    formData.append('certificateKind', options.certificateKind);
  }
  if (options?.certificatePassword) {
    formData.append('certificatePassword', options.certificatePassword);
  }

  const token = getAuthToken();
  const response = await fetch(`${API_CONFIG.BASE_URL}/doctors/${doctorId}/documents`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  const data = await response.json();
  if (!response.ok || data.success === false) {
    return { success: false, error: data.error || 'Erro ao fazer upload' };
  }
  return { success: true, data: data.data };
}

/** Replace an existing document (re-upload to R2, status resets to PENDING_REVIEW) */
export async function replaceDoctorDocument(
  doctorId: string,
  documentId: string,
  file: File,
  options?: {
    currentPassword?: string;
    certificatePassword?: string;
    certificateKind?: 'A1' | 'A2' | 'A3' | 'A4';
  },
): Promise<ApiResponse<DoctorDocumentDTO>> {
  const formData = new FormData();
  formData.append('file', file);
  if (options?.currentPassword) {
    formData.append('currentPassword', options.currentPassword);
  }
  if (options?.certificatePassword) {
    formData.append('certificatePassword', options.certificatePassword);
  }
  if (options?.certificateKind) {
    formData.append('certificateKind', options.certificateKind);
  }

  const token = getAuthToken();
  const response = await fetch(
    `${API_CONFIG.BASE_URL}/doctors/${doctorId}/documents/${documentId}`,
    {
      method: 'PATCH',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    },
  );

  const data = await response.json();
  if (!response.ok || data.success === false) {
    return { success: false, error: data.error || 'Erro ao substituir documento' };
  }
  return { success: true, data: data.data };
}

/** Unlock digital certificate view with password (returns temporary signed URL) */
export async function unlockDoctorDocumentView(
  doctorId: string,
  documentId: string,
  password: string,
): Promise<ApiResponse<DocumentViewUrlDTO>> {
  return apiClient.post(`/doctors/${doctorId}/documents/${documentId}/unlock-view`, {
    password,
  });
}

/** Delete a document (removes from R2 and database) */
export async function deleteDoctorDocument(
  doctorId: string,
  documentId: string,
): Promise<ApiResponse<void>> {
  return apiClient.delete(`/doctors/${doctorId}/documents/${documentId}`);
}

/** Resolve full URL for doctor avatar (or null for fallback) */
export function getDoctorAvatarSrc(
  doctor: Pick<DoctorDTO, 'id' | 'avatarUrl' | 'hasAvatar'>,
): string | null {
  if (doctor.avatarUrl) {
    if (doctor.avatarUrl.startsWith('http://') || doctor.avatarUrl.startsWith('https://')) {
      return doctor.avatarUrl;
    }
    const path = doctor.avatarUrl.startsWith('/') ? doctor.avatarUrl : `/${doctor.avatarUrl}`;
    return `${API_CONFIG.BASE_URL}${path}`;
  }
  if (doctor.hasAvatar) {
    return `${API_CONFIG.BASE_URL}/doctors/${doctor.id}/avatar`;
  }
  return null;
}

/** Upload or replace doctor profile photo */
export async function uploadDoctorAvatar(
  doctorId: string,
  file: File,
): Promise<ApiResponse<DoctorDTO>> {
  const formData = new FormData();
  formData.append('file', file);

  const token = getAuthToken();
  const response = await fetch(`${API_CONFIG.BASE_URL}/doctors/${doctorId}/avatar`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  const data = await response.json();
  if (!response.ok || data.success === false) {
    return {
      success: false,
      error: data.error || data.message || 'Erro ao enviar foto de perfil',
    };
  }
  return { success: true, data: data.data };
}

/** Remove doctor profile photo */
export async function deleteDoctorAvatar(doctorId: string): Promise<ApiResponse<DoctorDTO>> {
  return apiClient.delete(`/doctors/${doctorId}/avatar`);
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin Doctor Approval
// ─────────────────────────────────────────────────────────────────────────────

export async function getPendingApprovalDoctors(): Promise<ApiResponse<DoctorDTO[]>> {
  return apiClient.get('/admin/dashboard/pending-approvals');
}

export async function getDoctorDocumentsForReview(
  doctorId: string,
): Promise<ApiResponse<DoctorDocumentDTO[]>> {
  return apiClient.get(`/admin/doctors/${doctorId}/documents`);
}

export async function getAdminDocumentViewUrl(
  doctorId: string,
  documentId: string,
): Promise<ApiResponse<DocumentViewUrlDTO>> {
  return apiClient.get(`/admin/doctors/${doctorId}/documents/${documentId}/view-url`);
}

export async function approveDoctorAdmin(doctorId: string): Promise<ApiResponse<unknown>> {
  return apiClient.post(`/admin/doctors/${doctorId}/approve`);
}

export async function rejectDoctorAdmin(
  doctorId: string,
  rejectionReason: string,
): Promise<ApiResponse<unknown>> {
  return apiClient.post(`/admin/doctors/${doctorId}/reject`, { rejectionReason });
}
